import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, Sparkles, Video, Database, List, Bug, ArrowRight, Copy, Check, MousePointerClick, Terminal, FileWarning } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import posthogIcon from '../assets/icons/posthog-logo-stacked.svg';
import posthogLightIcon from '../assets/icons/posthog-light.png';

const PostHogIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const phIcon = isDark ? posthogLightIcon : posthogIcon;
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connectedHost, setConnectedHost] = useState('');
  const [connectedProjectId, setConnectedProjectId] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [exampleChannel, setExampleChannel] = useState('');
  const [exampleTeam, setExampleTeam] = useState('');
  const [lastWebhookAt, setLastWebhookAt] = useState<string | null>(null);
  const [webhookEventCount, setWebhookEventCount] = useState(0);

  const [host, setHost] = useState('https://us.posthog.com');
  const [projectId, setProjectId] = useState('');
  const [apiKey, setApiKey] = useState('');

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getPostHogStatus();
      if (data?.connected) {
        setConnected(true);
        setConnectedHost(data.host || '');
        setConnectedProjectId(data.project_id || '');
        setLastWebhookAt(data.last_webhook_at || null);
        setWebhookEventCount(data.webhook_event_count || 0);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectPostHog({
        host: host || 'https://us.posthog.com',
        project_id: projectId,
        api_key: apiKey,
      });
      setSuccessMsg('PostHog connected successfully!');
      setApiKey('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectPostHog();
      setConnected(false);
      setConnectedHost('');
      setConnectedProjectId('');
      setSuccessMsg('PostHog disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testPostHog();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to project: ${data.project_name || connectedProjectId}` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}

      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={phIcon} alt="PostHog" className="w-10 h-6 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  PostHog
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Session replay analysis, AI summaries, and event queries</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Host</span>
                <p className="font-medium">{connectedHost}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Project ID</span>
                <p className="font-medium">{connectedProjectId}</p>
              </div>
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              Webhook Setup
              {lastWebhookAt ? (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">{webhookEventCount.toLocaleString()} event{webhookEventCount !== 1 ? 's' : ''} received</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground text-[10px] font-normal">No events yet</Badge>
              )}
            </CardTitle>
            <CardDescription>To use PostHog triggers (Session Error, Console Error, Rage Click), configure PostHog to send events to Kestrel.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Webhook URL */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Webhook URL</p>
              <div className="flex items-center gap-2">
                <code className="bg-background px-2.5 py-1.5 rounded text-sm border flex-1 truncate select-all">{`${window.location.origin}/api/webhooks/posthog`}</code>
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/posthog`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}>
                  {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>

            {/* Steps */}
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                <span>In PostHog, go to <strong className="text-foreground">Data → Destinations</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                <span>Click <strong className="text-foreground">New Destination</strong> and select <strong className="text-foreground">HTTP Webhook</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                <span>Paste the <strong className="text-foreground">Webhook URL</strong> above. Set <strong className="text-foreground">Method</strong> to <strong className="text-foreground">POST</strong>. Leave the JSON Body as default.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                <span>Leave <strong className="text-foreground">Filters</strong> empty — Kestrel automatically routes events to the correct workflows based on your trigger configuration (e.g., only <code className="text-xs bg-muted px-1 rounded">$exception</code> or <code className="text-xs bg-muted px-1 rounded">rage_click</code> events).</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">5</span>
                <span>Click <strong className="text-foreground">Create & enable</strong>, then use <strong className="text-foreground">Test function</strong> to verify events are received.</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these PostHog actions and triggers in your workflows to automate session replay analysis and event monitoring.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Session Error / Exception', desc: 'Triggers when PostHog captures a frontend JavaScript error from a user session.', Icon: Bug },
                  { name: 'Console Error', desc: 'Triggers when PostHog captures a console.error() call during a session recording.', Icon: Terminal },
                  { name: 'Log Error Alert', desc: 'Triggers when PostHog Logs detects ERROR/FATAL level application logs. Kestrel auto-creates the alert on PostHog.', Icon: FileWarning },
                  { name: 'Rage Click', desc: 'Triggers when PostHog detects a rage click in a user session.', Icon: MousePointerClick },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Get Session Summary', desc: 'AI-generated summary of session recordings to surface patterns, errors, and user behavior.', Icon: Sparkles },
                  { name: 'Get Session Recording', desc: 'Retrieve metadata and replay URL for a specific session recording.', Icon: Video },
                  { name: 'Query Events', desc: 'Run HogQL queries against PostHog event data for custom analysis.', Icon: Database },
                  { name: 'List Session Recordings', desc: 'List recent session recordings, optionally filtered by date.', Icon: List },
                  { name: 'Get Error Issue', desc: 'Retrieve details of a PostHog error tracking issue including stack traces.', Icon: Bug },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a customer experiences a rage click or error during checkout, get the AI summary of their session, DM the{' '}
              <span className="inline-flex items-baseline">
                #<input
                  value={exampleChannel}
                  onChange={e => setExampleChannel(e.target.value)}
                  placeholder="channel"
                  style={{ width: `${Math.max(exampleChannel.length || 7, 7)}ch` }}
                  className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
                />
              </span>{' '}
              Slack channel with the summary and session replay link, and create a Jira ticket tagged to the{' '}
              <input
                value={exampleTeam}
                onChange={e => setExampleTeam(e.target.value)}
                placeholder="team"
                style={{ width: `${Math.max(exampleTeam.length || 4, 4)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}
              team.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleChannel || !exampleTeam}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a customer experiences a rage click or error during checkout, get the AI summary of their session, DM the #${exampleChannel} Slack channel with the summary and session replay link, and create a Jira ticket tagged to the ${exampleTeam} team.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={phIcon} alt="PostHog" className="w-10 h-6 object-contain" />
              <div>
                <CardTitle>Connect PostHog</CardTitle>
                <CardDescription>Connect your PostHog instance to enable session replay analysis, AI summaries, and event queries in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="ph-host" className="text-sm">PostHog Host URL</Label>
              <Input
                id="ph-host"
                value={host}
                onChange={e => setHost(e.target.value)}
                placeholder="https://us.posthog.com"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Use https://eu.posthog.com for EU, or your self-hosted URL.</p>
            </div>
            <div>
              <Label htmlFor="ph-project" className="text-sm">Project ID</Label>
              <Input
                id="ph-project"
                value={projectId}
                onChange={e => setProjectId(e.target.value)}
                placeholder="Your PostHog project ID"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Found in PostHog → Settings.</p>
            </div>
            <div>
              <Label htmlFor="ph-key" className="text-sm">Personal API Key</Label>
              <Input
                id="ph-key"
                type="password"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="phx_..."
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Create one at PostHog → Settings → Personal API Keys. Requires <code className="text-xs">project:read</code>, <code className="text-xs">session_recording:write</code>, <code className="text-xs">query:read</code>, and <code className="text-xs">error_tracking:read</code> scopes.</p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !projectId || !apiKey}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect PostHog
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PostHogIntegration;
